import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, Loader2, XCircle, BedDouble } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthPanel } from "@/components/site/AuthPanel";

const statusColor = {
  Confirmed: "bg-secondary/15 text-secondary border-secondary/30",
  "Checked-In": "bg-primary/15 text-primary border-primary/30",
  Cancelled: "bg-destructive/15 text-destructive border-destructive/30",
};
const today = () => new Date().toISOString().split("T")[0];

export default function MyBookings() {
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState(null);

  const load = useCallback(() => api.get("/my-bookings").then(({ data }) => setBookings(data)).catch(() => setBookings([])), []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const cancel = async (ref) => {
    if (!window.confirm("Cancel this booking? This cannot be undone.")) return;
    try {
      await api.put(`/my-bookings/${ref}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const upcoming = (bookings || []).filter((b) => b.check_out >= today() && b.status !== "Cancelled");
  const past = (bookings || []).filter((b) => !(b.check_out >= today() && b.status !== "Cancelled"));

  return (
    <div className="min-h-screen bg-background" data-testid="my-bookings-page">
      <div className="px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto py-8 lg:py-12">
        <Link to="/" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-primary" data-testid="my-bookings-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
        <span className="mt-6 block text-sm font-semibold uppercase tracking-[0.15em] text-primary">Your Stays</span>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold">My Bookings</h1>

        {!user ? (
          <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8 max-w-sm" data-testid="my-bookings-signin">
            <p className="text-muted-foreground text-sm mb-4">Sign in or create an account to see your bookings.</p>
            <AuthPanel returnPath="/my-bookings" testPrefix="my-bookings" />
          </div>
        ) : bookings === null ? (
          <Loader2 className="mt-10 h-6 w-6 animate-spin text-primary" />
        ) : bookings.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-border bg-card p-8 text-center" data-testid="my-bookings-empty">
            <BedDouble className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-3 text-muted-foreground">No bookings yet. Your next Nagpur stay awaits.</p>
            <Link to="/#rooms"><Button className="mt-4 bg-primary hover:bg-primary/90">Browse Rooms</Button></Link>
          </div>
        ) : (
          <>
            <Section title="Upcoming" items={upcoming} onCancel={cancel} empty="No upcoming stays." />
            <Section title="Past & Cancelled" items={past} empty="No past stays." />
          </>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, items, onCancel, empty }) => (
  <div className="mt-8">
    <h2 className="text-base md:text-lg font-semibold">{title}</h2>
    {items.length === 0 && <p className="mt-2 text-sm text-muted-foreground">{empty}</p>}
    <div className="mt-3 space-y-3">
      {items.map((b, i) => (
        <motion.div key={b.booking_ref} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
          className="rounded-2xl border border-border bg-card p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`my-booking-${b.booking_ref}`}>
          <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><CalendarCheck className="h-5 w-5" /></span>
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{b.room_name}</span>
              <Badge variant="outline" className={statusColor[b.status]} data-testid={`my-booking-status-${b.booking_ref}`}>{b.status}</Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">{b.check_in} → {b.check_out} · {b.nights} night{b.nights > 1 ? "s" : ""} · {b.guests} guest{b.guests > 1 ? "s" : ""}</div>
            <div className="text-xs font-mono text-muted-foreground mt-0.5">Ref {b.booking_ref}{b.coupon_code ? ` · ${b.coupon_code} applied` : ""}</div>
          </div>
          <div className="text-right">
            <div className="font-mono font-bold text-secondary">₹{b.total}</div>
            <div className="text-[11px] text-muted-foreground">pay at hotel</div>
          </div>
          {onCancel && b.status === "Confirmed" && b.check_in > today() && (
            <Button size="sm" variant="outline" onClick={() => onCancel(b.booking_ref)} className="gap-1.5 text-destructive hover:text-destructive" data-testid={`cancel-booking-${b.booking_ref}`}>
              <XCircle className="h-4 w-4" /> Cancel
            </Button>
          )}
        </motion.div>
      ))}
    </div>
  </div>
);
